import { createClient } from "@supabase/supabase-js";

type AnyRecord = Record<string, any>;

type SupabaseLikeClient = AnyRecord;

declare global {
  interface Window {
    supabase?: SupabaseLikeClient;
    DRABPE_CONFIG?: {
      SUPABASE_URL?: string;
      SUPABASE_ANON_KEY?: string;
    };
  }
}

export function getSupabaseClient(): SupabaseLikeClient {
  if (typeof window !== "undefined" && !window.supabase) {
    const config = window.DRABPE_CONFIG;
    if (config?.SUPABASE_URL && config?.SUPABASE_ANON_KEY) {
      // Initialize synchronously from the Hostinger runtime config. This avoids
      // a blank app when a separate CDN bootstrap script has not finished yet.
      window.supabase = createClient(config.SUPABASE_URL, config.SUPABASE_ANON_KEY) as SupabaseLikeClient;
    }
  }

  if (typeof window === "undefined" || !window.supabase) {
    throw new Error("Supabase client is not available. Make sure client/index.html initializes window.supabase before the app bundle loads.");
  }

  return window.supabase;
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function toProfileUser(profileRow: AnyRecord | null | undefined, authUser?: AnyRecord | null) {
  if (!authUser && !profileRow) return null;

  return {
    id: authUser?.id ?? profileRow?.user_id,
    username: profileRow?.username ?? authUser?.email?.split("@")[0] ?? "member",
    email: authUser?.email ?? profileRow?.email ?? "",
    role: profileRow?.role ?? "user",
    avatarUrl: profileRow?.avatar_url ?? null,
  };
}

export function toCategory(categoryRow: AnyRecord | null | undefined) {
  if (!categoryRow) return null;

  return {
    id: categoryRow.id,
    name: categoryRow.name,
    slug: categoryRow.slug,
    icon: categoryRow.icon ?? null,
    description: categoryRow.description ?? null,
  };
}

export function toBusiness(row: AnyRecord) {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    categoryId: row.category_id ?? row.categoryId ?? null,
    address: row.address ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    country: row.country ?? null,
    postalCode: row.postal_code ?? row.postalCode ?? null,
    latitude: row.latitude ?? null,
    longitude: row.longitude ?? null,
    phone: row.phone ?? null,
    email: row.email ?? null,
    hideEmail: row.hide_email ?? row.hideEmail ?? false,
    website: row.website ?? null,
    logoUrl: row.logo_url ?? row.logoUrl ?? null,
    imageUrls: row.image_urls ?? row.imageUrls ?? [],
    galleryUrls: row.gallery_urls ?? row.galleryUrls ?? [],
    galleryApproved: row.gallery_approved ?? row.galleryApproved ?? false,
    tags: row.tags ?? [],
    businessHours: row.business_hours ?? row.businessHours ?? null,
    linkedinUrl: row.linkedin_url ?? row.linkedinUrl ?? null,
    facebookUrl: row.facebook_url ?? row.facebookUrl ?? null,
    instagramUrl: row.instagram_url ?? row.instagramUrl ?? null,
    isOnline: row.is_online ?? row.isOnline ?? false,
    isInPerson: row.is_in_person ?? row.isInPerson ?? true,
    status: row.status,
    isArchived: row.is_archived ?? row.isArchived ?? false,
    isFeatured: row.is_featured ?? row.isFeatured ?? false,
    isSponsored: row.is_sponsored ?? row.isSponsored ?? false,
    ownerId: row.owner_id ?? row.ownerId ?? null,
    ownerPhotoUrl: row.owner_photo_url ?? row.ownerPhotoUrl ?? null,
    ownerDisplayName: row.owner_display_name ?? row.ownerDisplayName ?? null,
    avgRating: row.avg_rating ?? row.avgRating ?? 0,
    reviewCount: row.review_count ?? row.reviewCount ?? 0,
    createdAt: row.created_at ?? row.createdAt ?? null,
    updatedAt: row.updated_at ?? row.updatedAt ?? null,
    category: toCategory(row.categories ?? row.category ?? null),
    owner: toProfileUser(row.profiles ?? row.owner ?? null),
  };
}

export function toReview(row: AnyRecord) {
  return {
    id: row.id,
    businessId: row.business_id ?? row.businessId,
    userId: row.user_id ?? row.userId,
    rating: row.rating,
    comment: row.comment ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    user: toProfileUser(row.profiles ?? row.user ?? null),
  };
}

export function toComment(row: AnyRecord) {
  return {
    id: row.id,
    businessId: row.business_id ?? row.businessId,
    userId: row.user_id ?? row.userId,
    parentId: row.parent_id ?? row.parentId ?? null,
    content: row.content,
    createdAt: row.created_at ?? row.createdAt ?? null,
    user: toProfileUser(row.profiles ?? row.user ?? null),
  };
}

export function toPublicUser(authUser: AnyRecord | null, profileRow: AnyRecord | null | undefined) {
  if (!authUser) return null;

  return {
    id: authUser.id,
    username: profileRow?.username ?? authUser.email?.split("@")[0] ?? "member",
    email: authUser.email ?? "",
    role: profileRow?.role ?? "user",
    avatarUrl: profileRow?.avatar_url ?? null,
  };
}

export type SupabaseLike = SupabaseLikeClient;
